// A driver's own service hours claims, on /board. What he claimed on Part B,
// where it stands, the figure decided, and the reason when it was changed or
// not accepted, so he never has to ask why his hours moved.
//
// Everything comes from driver_portal_service_hours_v1, which resolves him by
// his linked account and returns his claims only (db/migrations/0600).
import { useEffect } from 'react'
import { useGetServiceHours } from '../../hooks/backend/driver'
import { Chip } from '../interviews/Chip'
import { formatDay, formatHours, statusTone, windowLabel, type DriverPortalClaim } from '../../utils/serviceHours'

type Result = { state: string; claims?: DriverPortalClaim[] }

export function ServiceHoursCard({ driverId }: { driverId: string | null }) {
  const q = useGetServiceHours<Result>()
  const load = q.trigger
  useEffect(() => {
    void load(driverId ? { driver_id: driverId } : {}, { skipCache: true }).catch(() => undefined)
  }, [load, driverId])

  const claims = q.data?.claims ?? []
  if (q.error) {
    return (
      <section className="rounded-xl border border-border bg-card p-4 text-sm">
        <h2 className="text-sm font-semibold">Service hours</h2>
        <p className="mt-1 text-muted-foreground">Your service hours could not load. {q.error}</p>
      </section>
    )
  }
  if (!q.data || claims.length === 0) return null

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <h2 className="text-sm font-semibold">Service hours</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Time you told us you took the car to a service. Once your Driver Servant accepts it, it counts towards your hours,
        up to the time you were offline on that shift.
      </p>
      <ul className="mt-3 divide-y divide-border">
        {claims.map((c) => (
          <li key={c.ref} className="py-2 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{formatDay(c.shift_date)}, {windowLabel(c.shift_window).toLowerCase()}</span>
              <span className="text-xs text-muted-foreground">you said {c.claimed_hours !== null ? formatHours(c.claimed_hours) : `"${c.claimed ?? ''}"`}</span>
              <Chip tone={statusTone(c.status)} className="ml-auto">{c.status_label}</Chip>
            </div>
            {c.decided_hours !== null && <p className="mt-1 text-xs">Counted: {formatHours(c.decided_hours)}</p>}
            {c.reason && <p className="mt-1 text-xs text-muted-foreground">Why: {c.reason}</p>}
          </li>
        ))}
      </ul>
    </section>
  )
}
